import {
  ChartUserOverrides,
  DataTableUserOverrides,
  WebDashboardContentSpec,
  WebDashboardContentSpecBlocksInner,
} from "@coeff/agent-types";
import { WebDashboardContentConfig } from "@coeff/api";
import { useCallback, useEffect, useRef } from "react";

import type { BlockEditUpdates } from "./BlockEditPopover";

/**
 * How manual block edits reach the backend. Two machines, one per edit rhythm:
 *
 * - `createBlockPatchQueue` — a serialized, coalescing save queue for edits with no natural
 *   end, i.e. data-table column drags. Running each change through applyDashboardChanges
 *   concurrently races: every call snapshots the config at start and saves independently, so
 *   an older save can land after — and overwrite — a newer payload, and a failed older
 *   save's rollback would erase a newer successful one. Instead: one save in flight at a
 *   time, and changes arriving during it are merged per block and drained as a single
 *   batched save. Leading edge, so the first drag saves immediately.
 * - `createBlockEditSession` — for the edit popover's click controls, which DO have a
 *   natural end (the popover closes). Every click updates the cache immediately; one save
 *   fires when the session ends. See its doc below.
 *
 * Both are React-free so they can be unit-tested under jest's node environment (there is no
 * jsdom or hook-rendering harness here); the `use*` functions are thin React adapters. Same
 * split as useSwipeDownToClose / createSwipeDownMachine.
 */

/**
 * Spec-level fields an instant edit may write on a block. Named after the spec fields (not
 * the popover's camelCase `BlockEditUpdates`) because these land verbatim on the block, and
 * typed field-by-field rather than as a loose record so a typo can't reach the spec.
 */
export interface BlockSpecPatch {
  title?: string;
  /** Metric cards carry their heading in `label`, every other block in `title`. */
  label?: string;
  description?: string;
  markdown?: string;
  chart_type?: string;
  icon?: string;
  show_icon?: boolean;
  show_comparison?: boolean;
  chart_overrides?: ChartUserOverrides;
  table_overrides?: DataTableUserOverrides;
}

/**
 * Maps the edit popover's camelCase updates onto spec field names. Shared by every write
 * path — the popover session's local applies, the AI routes' markdown save, and the text-block
 * popover's save — so they can never disagree about where a field lands.
 *
 * Metric cards carry their heading in `label`, every other block in `title`.
 */
export const buildBlockSpecPatch = (
  blockType: string,
  updates: BlockEditUpdates
): BlockSpecPatch => {
  const patch: BlockSpecPatch = {};
  if (updates.title !== undefined) {
    if (blockType === "metric_card") {
      patch.label = updates.title;
    } else {
      patch.title = updates.title;
    }
  }
  if (updates.description !== undefined) {
    patch.description = updates.description;
  }
  if (updates.markdown !== undefined) {
    patch.markdown = updates.markdown;
  }
  if (updates.chartType !== undefined) {
    patch.chart_type = updates.chartType;
  }
  if (updates.icon !== undefined) {
    patch.icon = updates.icon;
  }
  if (updates.showIcon !== undefined) {
    patch.show_icon = updates.showIcon;
  }
  if (updates.showComparison !== undefined) {
    patch.show_comparison = updates.showComparison;
  }
  if (updates.chartOverrides !== undefined) {
    patch.chart_overrides = updates.chartOverrides;
  }
  return patch;
};

export interface BlockPatchQueueConfig {
  /**
   * WebDashboardViewer's optimistic-save helper. Resolves after the save lands OR after a
   * failure has been rolled back — it swallows the error and invokes onError rather than
   * rejecting, which is why the queue never needs a catch.
   */
  applyDashboardChanges: (
    computeNewConfig: (
      currentConfig: WebDashboardContentConfig,
      contentSpec: WebDashboardContentSpec
    ) => WebDashboardContentConfig,
    options?: { onError?: () => void }
  ) => Promise<void>;
  /**
   * Undo a rolled-back `table_overrides` patch by putting the values back in the session
   * drag layer. Only tables need this — chart and KPI controls read their value from the
   * spec, so a rollback heals them automatically.
   */
  restoreTableSessionOverrides: (blockId: string, overrides: DataTableUserOverrides) => void;
}

export interface BlockPatchQueue {
  queueBlockPatch: (blockId: string, patch: BlockSpecPatch) => void;
}

/**
 * Applies per-block patches onto a content config. Shared by the save queue (table drags)
 * and the edit session's local apply (popover clicks), so both write a patch the same way.
 */
export function applyBlockPatches(
  currentConfig: WebDashboardContentConfig,
  contentSpec: WebDashboardContentSpec,
  patches: Map<string, BlockSpecPatch>
): WebDashboardContentConfig {
  return {
    ...currentConfig,
    content_spec: {
      ...contentSpec,
      blocks: contentSpec.blocks.map(block => {
        const patch = patches.get(block.block_id);
        if (!patch) {
          return block;
        }
        // `table_overrides` keeps the type guard it had before this queue was generalized:
        // it is the one patch field driven by DOM events (AG Grid column callbacks) rather
        // than by a block-typed editor, so a mis-keyed payload is the one that could
        // realistically write onto a non-table block.
        let safePatch = patch;
        if (patch.table_overrides && block.type !== "data_table") {
          // Delete rather than set undefined, which would clobber a real value.
          safePatch = { ...patch };
          delete safePatch.table_overrides;
        }
        // The blocks union has no common shape that accepts a partial spread; the patch's
        // own field types are what guarantee the values are valid.
        return { ...block, ...safePatch } as WebDashboardContentSpecBlocksInner;
      }),
    },
  };
}

export function createBlockPatchQueue(config: BlockPatchQueueConfig): BlockPatchQueue {
  let inFlight = false;
  const pending = new Map<string, BlockSpecPatch>();

  const flush = async (): Promise<void> => {
    if (inFlight || pending.size === 0) {
      return;
    }
    inFlight = true;
    const batch = new Map(pending);
    pending.clear();
    try {
      await config.applyDashboardChanges(
        (currentConfig, contentSpec) => applyBlockPatches(currentConfig, contentSpec, batch),
        {
          // The optimistic update's fingerprint change clears the session drag layer (fine
          // when the save lands — persisted then carries the same values). If the backend
          // rejects and the update rolls back, put the values back so drags aren't lost.
          onError: () => {
            for (const [blockId, patch] of batch) {
              if (patch.table_overrides) {
                config.restoreTableSessionOverrides(blockId, patch.table_overrides);
              }
            }
          },
        }
      );
    } finally {
      inFlight = false;
      // Patches queued while this save was in flight — drain them. Also recovers cleanly
      // after a failure, since the next batch recomputes from the rolled-back config.
      if (pending.size > 0) {
        void flush();
      }
    }
  };

  return {
    queueBlockPatch: (blockId, patch) => {
      // MERGE rather than replace: a patch names only the fields that changed, so replacing
      // would drop an earlier field queued in the same burst. `table_overrides` payloads are
      // a block's complete override state, so merging still overwrites that one key
      // wholesale — same semantics as before this queue was generalized.
      const previous = pending.get(blockId);
      pending.set(blockId, previous ? { ...previous, ...patch } : patch);
      void flush();
    },
  };
}

export function useBlockPatchQueue(config: BlockPatchQueueConfig): BlockPatchQueue {
  // Held in a ref so the queue (and any in-flight save) survives re-renders, while still
  // calling the latest applyDashboardChanges.
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  });

  const queueRef = useRef<BlockPatchQueue>();
  if (!queueRef.current) {
    queueRef.current = createBlockPatchQueue({
      applyDashboardChanges: (compute, options) =>
        configRef.current.applyDashboardChanges(compute, options),
      restoreTableSessionOverrides: (blockId, overrides) =>
        configRef.current.restoreTableSessionOverrides(blockId, overrides),
    });
  }

  const queueBlockPatch = useCallback((blockId: string, patch: BlockSpecPatch) => {
    queueRef.current?.queueBlockPatch(blockId, patch);
  }, []);

  return { queueBlockPatch };
}

/**
 * One save per block edit popover session.
 *
 * The popover's click controls (icon, chart type, legend, axes, ...) write to the query cache
 * on every click so the block updates immediately, but nothing goes to the backend until the
 * session ends — Done, outside click, Escape, or unmount. The session then PUTs once, with the
 * snapshot taken before the FIRST click as the undo entry and the rollback target. One S3
 * version, one undo step, one concurrency check per session, and no invisible pending state
 * (the queue above holds later clicks back while a save is in flight; this never does).
 *
 * `TSnapshot` is whatever the viewer needs to persist against — the full dashboard response,
 * for its version token. Generic so the machine stays React- and query-free for the node
 * jest project, same split as createBlockPatchQueue.
 */
export interface BlockEditSessionConfig<TSnapshot> {
  /** The dashboard as currently cached; undefined when nothing is loaded. */
  getSnapshot: () => TSnapshot | undefined;
  /** Optimistic cache write for one block. Synchronous — the control re-renders this tick. */
  applyLocal: (blockId: string, patch: BlockSpecPatch) => void;
  /** Whether the block differs between the session's baseline and the current cache. */
  hasBlockChanged: (baseline: TSnapshot, blockId: string) => boolean;
  /**
   * PUT the current cache against `baseline`. Resolves after the save lands or after a
   * failure has been rolled back — it must not reject.
   */
  persist: (baseline: TSnapshot) => Promise<void>;
}

export interface BlockEditSession {
  applyLocal: (blockId: string, patch: BlockSpecPatch) => void;
  /** Ends the block's session. No-op without one, or when nothing changed. Idempotent. */
  commit: (blockId: string) => Promise<void>;
  /** Ends every open session (page unload). */
  commitAll: () => Promise<void>;
  hasOpenSession: (blockId: string) => boolean;
}

export function createBlockEditSession<TSnapshot>(
  config: BlockEditSessionConfig<TSnapshot>
): BlockEditSession {
  // Baseline per block: the cache as it was before the session's first local apply.
  const baselines = new Map<string, TSnapshot>();

  const commit = async (blockId: string): Promise<void> => {
    const baseline = baselines.get(blockId);
    if (baseline === undefined) {
      return;
    }
    // Clear BEFORE persisting: a click that arrives while the PUT is in flight starts a fresh
    // session whose baseline already includes this one's edits, so the two never overlap.
    baselines.delete(blockId);
    if (!config.hasBlockChanged(baseline, blockId)) {
      return;
    }
    await config.persist(baseline);
  };

  return {
    applyLocal: (blockId, patch) => {
      if (!baselines.has(blockId)) {
        const snapshot = config.getSnapshot();
        if (snapshot === undefined) {
          return;
        }
        baselines.set(blockId, snapshot);
      }
      config.applyLocal(blockId, patch);
    },
    commit,
    commitAll: async () => {
      await Promise.all(Array.from(baselines.keys()).map(commit));
    },
    hasOpenSession: blockId => baselines.has(blockId),
  };
}

export function useBlockEditSession<TSnapshot>(
  config: BlockEditSessionConfig<TSnapshot>
): BlockEditSession {
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  });

  const sessionRef = useRef<BlockEditSession>();
  if (!sessionRef.current) {
    sessionRef.current = createBlockEditSession<TSnapshot>({
      getSnapshot: () => configRef.current.getSnapshot(),
      applyLocal: (blockId, patch) => configRef.current.applyLocal(blockId, patch),
      hasBlockChanged: (baseline, blockId) => configRef.current.hasBlockChanged(baseline, blockId),
      persist: baseline => configRef.current.persist(baseline),
    });
  }

  // Stable identities so consumers can put them in dependency arrays.
  const applyLocal = useCallback(
    (blockId: string, patch: BlockSpecPatch) => sessionRef.current!.applyLocal(blockId, patch),
    []
  );
  const commit = useCallback((blockId: string) => sessionRef.current!.commit(blockId), []);
  const commitAll = useCallback(() => sessionRef.current!.commitAll(), []);
  const hasOpenSession = useCallback(
    (blockId: string) => sessionRef.current!.hasOpenSession(blockId),
    []
  );

  return { applyLocal, commit, commitAll, hasOpenSession };
}
